import { useContext, useEffect, useRef, useState } from "react";
import Head from "./Head";
import Footer from "../components/Footer";
import RenderSvg from "../components/RenderSvg";
import PdfCanvasPreview from "../components/PdfCanvasPreview";
import GlobalConfig from "../context/GlobalConfig";
import JourneyConfig from "../context/JourneyConfig";
import { uploadFileToS3 } from "../utils/base64ToUrlUtility";
import { languageData } from "../constants/languageData";
import "./userDetails.css";
import "./supportingDocument.css";

/** Matches FileType.SUPPORTING_DOC in one-journey-backend's s3Constants. */
const SUPPORTING_DOC_FILE_TYPE = "supportingDoc";

/**
 * Supporting documents are routinely PDFs — a utility bill, a bank statement, a sponsorship
 * letter — so unlike the ID capture steps this accepts documents as well as images. The list
 * mirrors ALLOWED_UPLOAD_MIME_TYPES in one-journey-backend, so the applicant is told at once
 * rather than failing at the presigned upload.
 */
const ACCEPTED_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Same escape hatch the document capture steps use: flag the error and re-enter at the top. */
function routeToErrorScreen(stepperFunc, context) {
  console.error(`[SupportingDocument] Routing to error screen | context=${context}`);
  sessionStorage.setItem("error", "true");
  sessionStorage.removeItem("errorCategory");
  sessionStorage.removeItem("errorReason");
  const currentStepFromStorage = sessionStorage.getItem("currentStep");
  stepperFunc(currentStepFromStorage ? parseInt(currentStepFromStorage) : 0);
}

/**
 * Supporting Document capture.
 *
 * Capture-only: the file is stored as-is for manual review and is never OCR'd, classified or
 * compared, so there is no analysis screen and no retry loop. The label shown to the applicant
 * comes from the flow config — it is the only thing telling them what to upload.
 *
 * When the flow marks the document optional the applicant may skip it and the journey still
 * completes; the step is recorded with no file.
 */
const SupportingDocument = ({ stepperFunc, steps, currentStep }) => {
  const { config } = useContext(GlobalConfig);
  const { updateJourney } = useContext(JourneyConfig);

  const language = sessionStorage.getItem("language") ?? "en";
  // User-facing strings live in languageData (English is the source of truth); non-English
  // journeys fall back to English until the localization pipeline translates the block — the
  // same pattern the rest of the journey uses.
  const t = languageData[language]?.supportingDocument ?? languageData.en.supportingDocument;
  const { supportingDocument } = config ?? {};
  // Every journey screen themes itself from branding.accentColor (Head, DocumentCapture,
  // CameraAccess, UnableToProcess). UserDetails' btnOneColor is a one-off that is not set on
  // these flows, which is why copying it produced a blue button instead of the brand colour.
  const accentColor = config?.branding?.accentColor;
  const label = supportingDocument?.label ?? "";
  const isRequired = supportingDocument?.required ?? false;

  const journeyId = sessionStorage.getItem("jId");
  const fileInputRef = useRef(null);

  // The label is admin-authored free text (10–140 chars). To keep the screen tidy it is
  // clamped to two lines, with a "Show more" toggle that only appears when the text actually
  // overflows — measured against the clamped height rather than guessed from length.
  const labelRef = useRef(null);
  const [isLabelExpanded, setIsLabelExpanded] = useState(false);
  const [isLabelClamped, setIsLabelClamped] = useState(false);

  const [file, setFile] = useState(null);
  // Object URL for the local preview only — never sent to the backend. The file itself is
  // uploaded directly to S3 (no base64). Revoked when it changes / on unmount to avoid leaks.
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isPdf = file?.type === "application/pdf";

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Detect whether the (clamped) label is being truncated so the "Show more" toggle is only
  // offered when it's needed. Re-measured when the label changes; the expanded state is left
  // out of the deps on purpose so expanding doesn't measure the now-unclamped height and hide
  // the toggle.
  useEffect(() => {
    const element = labelRef.current;
    if (!element) return;
    setIsLabelClamped(element.scrollHeight > element.clientHeight + 1);
  }, [label]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!ACCEPTED_MIME_TYPES.includes(selectedFile.type)) {
      setError(t.errorInvalidType);
      return;
    }
    if (selectedFile.size > MAX_FILE_BYTES) {
      setError(t.errorTooLarge);
      return;
    }

    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setError("");
  };

  const advance = () => stepperFunc(steps?.[0] ?? currentStep + 1);

  const handleSubmit = async () => {
    if (!file) {
      setError(t.errorNoFile);
      return;
    }

    setIsLoading(true);
    try {
      // The file is uploaded straight to S3 via a presigned POST — no base64, backend only
      // stores the returned key. Retries with a fresh presigned URL happen inside
      // uploadFileToS3, so a non-null error here is terminal.
      const { s3Key, error: uploadError } = await uploadFileToS3(
        file,
        journeyId,
        SUPPORTING_DOC_FILE_TYPE
      );

      // Rate-limit cap hit → the journey is already TERMINATED on the backend. Ping update-journey
      // so Settings switches to the VerificationTerminated screen, matching the ID capture steps
      // rather than showing the generic error screen.
      if (uploadError && uploadError.terminateJourney) {
        try {
          await updateJourney("SUPPORTING_DOC", {}, currentStep);
        } catch (signalError) {
          console.error("[SupportingDocument] Failed to surface terminated state", signalError);
        }
        return;
      }

      if (uploadError) {
        console.error("[SupportingDocument] Upload failed", uploadError);
        setIsLoading(false);
        routeToErrorScreen(stepperFunc, `upload-${uploadError.code}`);
        return;
      }

      await updateJourney(
        "SUPPORTING_DOC",
        { supportingDoc: s3Key },
        currentStep
      );
      advance();
    } catch (submitError) {
      console.error("[SupportingDocument] Failed to submit", submitError);
      setIsLoading(false);
      routeToErrorScreen(stepperFunc, "submit");
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      // The backend records the step with no file and moves the journey on.
      await updateJourney("SUPPORTING_DOC", {}, currentStep);
      advance();
    } catch (skipError) {
      console.error("[SupportingDocument] Failed to skip", skipError);
      setIsLoading(false);
      routeToErrorScreen(stepperFunc, "skip");
    }
  };

  const isSubmitDisabled = isLoading || !file;

  return (
    <>
      <Head
        showBackIcon
        handleBack={() => stepperFunc(steps?.[2])}
        showLanguages={false}
        style={{ zIndex: "100" }}
        stepperFunc={stepperFunc}
      />

      <div className="parent-container supportingDocument">
        <div className="main">
          {/* Left Section — preview of the chosen file, or the illustration until one is chosen */}
          <div className="left-section">
            {!file && (
              <div style={{ display: "block" }}>
                <RenderSvg config={config} svgType={"selectDocument"} id="selectDocument" />
              </div>
            )}
            {file && !isPdf && <img src={previewUrl} alt="Supporting document preview" />}
            {file && isPdf && (
              <div
                className="flex flex-col w-full h-full min-h-[400px]"
                style={{ gap: 8, overflow: "visible" }}
              >
                <PdfCanvasPreview
                  file={previewUrl}
                  openPdfLabel={languageData[language]?.pdfOpenLabel ?? "Open PDF"}
                  previewHint={
                    languageData[language]?.pdfPreviewHint ??
                    "If preview doesn't appear, you can still continue."
                  }
                  onError={(e) =>
                    console.error("[SupportingDocument] react-pdf preview failed:", e)
                  }
                />
              </div>
            )}
          </div>

          {/* Right Section — what to upload, and the picker */}
          <div className="right-section">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="md:mb-[32px] mb-[20px]">
                  <div
                    ref={labelRef}
                    title={label || undefined}
                    className={`text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start break-words${
                      isLabelExpanded ? "" : " line-clamp-2"
                    }`}
                  >
                    {label || t.headingFallback}
                  </div>
                  {isLabelClamped && (
                    <button
                      type="button"
                      onClick={() => setIsLabelExpanded((prev) => !prev)}
                      className="block mx-auto md:mx-0 mt-[8px] text-[14px] font-medium underline"
                      style={{ color: accentColor || "#656565" }}
                    >
                      {isLabelExpanded
                        ? t.showLess ?? "Show less"
                        : t.showMore ?? "Show more"}
                    </button>
                  )}
                </div>

                <div className="text-[14px] md:text-[16px] text-[#656565] text-center md:text-start mb-[32px]">
                  {t.subHeading}
                </div>

                <div className="inputGroup">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_MIME_TYPES.join(",")}
                    onChange={handleFileChange}
                    className="supportingDocInput"
                    data-testid="supporting-document-input"
                    aria-label={label || "Supporting document"}
                  />

                  <button
                    type="button"
                    className="supportingDocPicker"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    {file ? file.name : t.choosePlaceholder}
                  </button>

                  {error && <span className="supportingDocError">{error}</span>}
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-8">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="proceedButton"
                  disabled={isSubmitDisabled}
                  aria-disabled={isSubmitDisabled}
                  style={{
                    backgroundColor: accentColor,
                    opacity: isSubmitDisabled ? 0.5 : 1,
                    cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  <span>{isLoading ? t.uploading : t.continue}</span>
                  <span className="arrowIcon">→</span>
                </button>

                {!isRequired && (
                  <button
                    type="button"
                    className="supportingDocSkip"
                    onClick={handleSkip}
                    disabled={isLoading}
                    style={{ color: accentColor }}
                  >
                    {t.skip}
                  </button>
                )}

                <Footer />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportingDocument;
